/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { useAirmetList, useSigmetList } from '../../api/hooks';
import type {
  Airmet,
  AirmetFromBackend,
  ProductConfig,
  ProductType,
  Sigmet,
  SigmetFromBackend,
} from '../../types';
import { getDefaultValidUntilValue } from '../ProductForms/ProductFormFields/ValidUntil';

interface State {
  isLoading: boolean;
  error: Error;
  result: SigmetFromBackend[] | AirmetFromBackend[];
}

interface UseApiProps extends State {
  fetchNewData: () => Promise<void>;
}

export const useProductApi = (productType: ProductType): UseApiProps => {
  const sigmetQuery = useSigmetList(productType !== 'sigmet');
  const airmetQuery = useAirmetList(productType !== 'airmet');

  const query = productType === 'sigmet' ? sigmetQuery : airmetQuery;

  const { data, isLoading, error, refetch } = query;

  return {
    result: data || [],
    isLoading,
    error: error as Error,
    fetchNewData: async (): Promise<void> => {
      await refetch();
    },
  };
};

const renewProduct = <T extends Airmet | Sigmet>(
  oldProduct: T,
  productConfig: ProductConfig,
): T => ({
  ...oldProduct,
  validDateStart: oldProduct.validDateEnd,
  validDateEnd: getDefaultValidUntilValue(
    productConfig,
    oldProduct.phenomenon,
    oldProduct.locationIndicatorATSR,
    oldProduct.validDateEnd,
  ),
  status: 'DRAFT',
  uuid: undefined,
  sequence: '-1',
});
export const renewAirmet = (
  oldAirmet: AirmetFromBackend,
  productConfig: ProductConfig,
): AirmetFromBackend => {
  const airmet = oldAirmet.airmet as Airmet;
  return {
    ...oldAirmet,
    uuid: undefined,
    canbe: ['DRAFTED', 'DISCARDED', 'PUBLISHED'],
    airmet: renewProduct(airmet, productConfig),
  };
};
export const renewSigmet = (
  oldSigmet: SigmetFromBackend,
  productConfig: ProductConfig,
): SigmetFromBackend => {
  const sigmet = oldSigmet.sigmet as Sigmet;
  return {
    ...oldSigmet,
    uuid: undefined,
    canbe: ['DRAFTED', 'DISCARDED', 'PUBLISHED'],
    sigmet: renewProduct(sigmet, productConfig),
  };
};
